import {
  booleanAttribute,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  SecurityContext,
  signal,
  viewChild,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { BlockBlobClient } from '@azure/storage-blob';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideTrash, lucideUpload } from '@ng-icons/lucide';
import { HlmButton } from '@spartan-ng/helm/button';
import { HlmIcon } from '@spartan-ng/helm/icon';
import { HlmProgress, HlmProgressIndicator } from '@spartan-ng/helm/progress';
import { toast } from 'ngx-sonner';
import { v7 as uuidv7 } from 'uuid';

import { ToastrErrorComponent } from '../toastr/toastr-error.component';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'file-upload',
  templateUrl: 'file-upload.component.html',
  styleUrls: ['file-upload.component.scss'],
  imports: [NgIcon, HlmButton, HlmProgress, HlmProgressIndicator, HlmIcon],
  providers: [provideIcons({ lucideTrash, lucideUpload })],
})
export class FileUploadComponent {
  /* Parameters */
  readonly acceptTypes = input('', { alias: 'accept' });
  readonly canChange = input<boolean, boolean | string>(true, { transform: booleanAttribute });
  readonly fileSource = input<string | null>(null, { alias: 'href' });
  readonly onFileChanged = output<File | undefined>({ alias: 'file-change' });
  readonly buttonText = input('Sélectionnez un fichier');
  readonly dragDropText = input('Glissez-déposez votre fichier ici');
  readonly hintText = input('');

  /* Internal behaviors */
  readonly fileInput = viewChild('fileInput');
  readonly choseFile = signal<File | undefined>(undefined);
  readonly hasFile = computed(() => this.choseFile() || this.fileSource());
  readonly fileName = computed(() => this.choseFile()?.name ?? this.#extractFileName(this.fileSource()));
  readonly fileSize = computed(() => (this.choseFile()?.size ? Math.round(this.choseFile()!.size / 1000) : undefined)); // TODO get size of remote file?
  readonly isFileRemote = computed(
    () => this.fileSource()?.startsWith('https://') || this.fileSource()?.startsWith('http://'),
  );
  readonly preview = signal('');
  readonly uploadProgress = signal(0);
  readonly uploadSucceeded = signal(false);
  readonly uploadFailed = signal(false);
  readonly #sanitizer = inject(DomSanitizer);

  constructor() {
    effect(() => {
      const fileSource = this.fileSource();

      if (fileSource) {
        this.preview.set(fileSource.replaceAll('+', '%2B'));
      }
    });
  }

  async upload(url: string): Promise<boolean> {
    const blocClient = new BlockBlobClient(url);
    try {
      const file = this.choseFile();
      if (file) {
        reportStatus('Uploading file...');
        blocClient.uploadData(file, {
          onProgress: (p) => this.uploadProgress.set((p.loadedBytes / file.size) * 100),
          blobHTTPHeaders: {
            blobContentType: file.type,
          },
        });
        this.uploadSucceeded.set(true);
        setTimeout(() => this.uploadSucceeded.set(false), 2500);
        reportStatus('Done.');
      }

      return true;
    } catch (error: any) {
      reportStatus(error.message);
      this.uploadFailed.set(true);
      setTimeout(() => this.uploadFailed.set(false), 2500);
      this.uploadProgress.set(0);

      const toastrId = uuidv7();

      toast.custom(ToastrErrorComponent, {
        componentProps: {
          message: "Un problème est survenu lors de la sauvegarde de l'image !",
          id: toastrId,
        },
        id: toastrId,
      });

      return false;
    }

    function reportStatus(message: string) {
      console.info(message);
    }
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
  }

  async onFileDrop(event: DragEvent) {
    const file = event.dataTransfer?.files[0];
    await this.#updateInformation(file);
    this.onFileChanged.emit(file);
  }

  async onFileChange(event: Event) {
    const file = (event.target as HTMLInputElement)?.files?.[0];
    await this.#updateInformation(file);
    this.onFileChanged.emit(file);
  }

  async remove(event: Event) {
    event.stopPropagation();
    await this.#updateInformation(undefined);
    this.uploadFailed.set(false);
    this.uploadSucceeded.set(false);
    this.preview.set('');
    this.onFileChanged.emit(undefined);
  }

  #extractFileName(url: string | null) {
    if (!url) return;
    let end = url.lastIndexOf('?');
    end = end === -1 ? url.length : end;
    const start = url.lastIndexOf('/', end);
    return decodeURIComponent(url.substring(start + 1, end));
  }

  async #updateInformation(file?: File) {
    this.choseFile.set(file);

    if (!file) return;
    if (this.acceptTypes().indexOf('image/') < 0) return;

    const buffer = await file.arrayBuffer();
    if (!buffer) return;

    const _arrayBufferToBase64 = (buffer: ArrayBuffer) => {
      var binary = '';
      var bytes = new Uint8Array(buffer);
      var len = bytes.byteLength;
      for (var i = 0; i < len; i++) {
        binary += String.fromCharCode(bytes[i]);
      }
      return window.btoa(binary);
    };

    let data: string | null = _arrayBufferToBase64(buffer);
    data = this.#sanitizer.sanitize(SecurityContext.NONE, data);
    data = 'data:image/jpg;base64,' + data;
    this.preview.set(data);
  }
}
